// Phase 2 — live-only, on-device photo capture for Neo's shared iPad kiosk.
// No file/gallery input is exposed. This is photo evidence, NOT face matching
// or biometric liveness verification.
import React, { useEffect, useRef, useState } from "react";

const MAX_EDGE_PX = 720;
const TARGET_BYTES = 320 * 1024;
const HARD_LIMIT_BYTES = 512 * 1024;

function cameraMessage(error) {
  switch (error?.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Camera permission was denied. Enable camera access for Neo in iPad Settings or Safari, then try again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No camera was found on this device.";
    case "NotReadableError":
    case "TrackStartError":
      return "The camera is busy. Close other apps using it, then try again.";
    case "SecurityError":
      return "Camera access requires a secure HTTPS page (or localhost).";
    default:
      return "Unable to start the camera. Check its permissions and try again.";
  }
}

function canvasToJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    if (!canvas.toBlob) {
      reject(new Error("This browser does not support camera photo capture."));
      return;
    }
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Unable to prepare the camera photo."))),
      "image/jpeg",
      quality
    );
  });
}

async function captureCompressedJpeg(video) {
  const width = video?.videoWidth || 0;
  const height = video?.videoHeight || 0;
  if (!width || !height) throw new Error("Camera is still starting. Please wait a moment.");

  // Restrict dimensions before encoding: low Storage cost, adequate face evidence.
  const scale = Math.min(1, MAX_EDGE_PX / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This device cannot process the camera photo.");

  // Mirror the front-camera capture to match the familiar selfie preview.
  context.translate(canvas.width, 0);
  context.scale(-1, 1);
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  try {
    let blob;
    for (const quality of [0.76, 0.62, 0.48]) {
      blob = await canvasToJpeg(canvas, quality);
      if (blob.size <= TARGET_BYTES) break;
    }
    if (!blob || blob.size > HARD_LIMIT_BYTES) {
      throw new Error("The photo could not be compressed enough. Please retake it.");
    }
    return new File([blob], `neo-clockin-${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

export default function LiveClockInCamera({ onCapture, onClose, staffName }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const activeRef = useRef(true);
  onCloseRef.current = onClose;
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    activeRef.current = true;
    let cancelled = false;
    let sessionStream = null;
    setLoading(true);
    setReady(false);
    setError("");

    const stop = () => {
      if (sessionStream) sessionStream.getTracks().forEach((track) => track.stop());
      if (streamRef.current === sessionStream) streamRef.current = null;
      if (videoRef.current?.srcObject === sessionStream) videoRef.current.srcObject = null;
    };

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setLoading(false);
        setError("This browser cannot open a live camera. Use an updated iPad browser over HTTPS.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 960 } },
        });
        sessionStream = stream;
        if (cancelled) {
          stop();
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) { stop(); return; }
        video.srcObject = stream;
        try { await video.play(); } catch (_) { /* play can await metadata on Safari */ }
        if (!cancelled) setLoading(false);
      } catch (cause) {
        if (!cancelled) {
          setError(cameraMessage(cause));
          setLoading(false);
        }
      }
    };

    const handleHidden = () => {
      if (document.visibilityState === "hidden") onCloseRef.current();
    };
    document.addEventListener("visibilitychange", handleHidden);
    start();
    return () => {
      cancelled = true;
      activeRef.current = false;
      document.removeEventListener("visibilitychange", handleHidden);
      stop();
    };
  }, [retry]);

  const handleCapture = async () => {
    if (saving || !ready || !streamRef.current) return;
    setSaving(true);
    setError("");
    try {
      const photo = await captureCompressedJpeg(videoRef.current);
      if (activeRef.current) onCapture(photo);
    } catch (cause) {
      if (activeRef.current) setError(cause?.message || "Photo capture failed. Please try again.");
    } finally {
      if (activeRef.current) setSaving(false);
    }
  };

  return (
    <div
      role="presentation"
      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Live clock-in photo"
        style={{ width: "min(100%, 510px)", maxHeight: "calc(100dvh - 24px)", overflowY: "auto", background: "#0b1930", color: "#fff", border: "1px solid #355071", borderRadius: 18, padding: 16, boxShadow: "0 12px 48px rgba(0,0,0,.45)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
          <div>
            <strong style={{ fontSize: 18 }}>Live clock-in photo</strong>
            <div style={{ fontSize: 13, color: "#b7cee9", marginTop: 3 }}>{staffName || "Staff"} · Front camera</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} style={{ background: "#24364f", color: "white", padding: "10px 16px", borderRadius: 10, border: "1px solid #445b78", cursor: "pointer" }}>Cancel</button>
        </div>

        <div style={{ background: "#030914", borderRadius: 14, overflow: "hidden", position: "relative", aspectRatio: "4 / 3", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onPlaying={() => setReady(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
          />
          {(loading || (!ready && !error)) && (
            <span style={{ position: "absolute", background: "rgba(0,0,0,.7)", borderRadius: 8, padding: 12 }}>Starting camera…</span>
          )}
        </div>

        <p style={{ color: "#c3d8ef", fontSize: 13, margin: "10px 0" }}>
          Position your face clearly in the frame. Only a new camera capture is accepted; saved photos cannot be selected.
        </p>
        {error && <p role="alert" style={{ color: "#fecaca", background: "#491b27", padding: 10, borderRadius: 8 }}>{error}</p>}
        {!ready && !loading && !error && (
          <button type="button" onClick={() => videoRef.current?.play().catch(() => setError("Camera preview could not start. Please try again."))}
            style={{ background: "#305b90", border: 0, borderRadius: 10, color: "white", padding: "10px 14px", marginBottom: 10 }}>
            Start camera preview
          </button>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button type="button" disabled={!ready || loading || saving || !!error} onClick={handleCapture}
            style={{ background: "#12a76b", opacity: !ready || loading || saving || !!error ? .55 : 1, color: "white", padding: "12px 18px", border: 0, borderRadius: 11, fontSize: 16, fontWeight: 800, flex: 1, cursor: "pointer" }}>
            {saving ? "Preparing photo…" : "📷 Capture photo"}
          </button>
          {error && (
            <button type="button" onClick={() => setRetry((n) => n + 1)} style={{ background: "#305b90", border: 0, borderRadius: 11, color: "white", padding: "12px 16px" }}>Try camera again</button>
          )}
        </div>
      </section>
    </div>
  );
}
